import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import Home from '../views/Home.vue'
import type { RouteLocationNormalized } from 'vue-router'

// Lazy-loaded components
const LoginView = () => import('@/views/LoginView.vue')
const DashboardView = () => import('@/views/DashboardView.vue')
const LoanApplicationView = () => import('@/views/LoanApplicationView.vue')
const LoanApplicationEditView = () => import('@/views/LoanApplicationEditView.vue')
const LoanApplicationUploadView = () => import('@/views/LoanApplicationUploadView.vue')
const PipelineView = () => import('@/views/PipelineView.vue')
const DocumentsView = () => import('@/views/DocumentsView.vue')
const MessagesView = () => import('@/views/MessagesView.vue')
const NotFoundView = () => import('@/views/errors/NotFoundView.vue')
const ForbiddenView = () => import('@/views/errors/ForbiddenView.vue')
const PageExpiredView = () => import('@/views/errors/PageExpiredView.vue')
const ServerErrorView = () => import('@/views/errors/ServerErrorView.vue')
const QuickPricerView = () => import('@/views/QuickPricerView.vue')
const ConditionsView = () => import('@/views/ConditionsView.vue')
const LoansView = () => import('@/views/LoansView.vue')
const BrokerPackageListView = () => import('@/views/BrokerPackageListView.vue')
const BrokerPackageView = () => import('@/views/BrokerPackageView.vue')
const ForgotPasswordView = () => import('@/views/ForgotPasswordView.vue')
const LoanDetailsView = () => import('@/views/LoanDetailsView.vue')
const ProfileView = () => import('@/views/profile/ProfileView.vue')
const ProfileSettingsView = () => import('@/views/profile/ProfileSettingsView.vue')
const ProfileCompanyView = () => import('@/views/profile/ProfileCompanyView.vue')
const MembersView = () => import('@/views/members/MembersView.vue')
const LoanSubmissionView = () => import('@/views/LoanSubmissionView.vue')
const ResourcesView = () => import('@/views/ResourcesView.vue')
const ExclusionaryListView = () => import('@/views/ExclusionaryListView.vue')
const AppraisalView = () => import('@/views/AppraisalView.vue')
const MortgageClauseView = () => import('@/views/MortgageClauseView.vue')
const ResourcesFormsView = () => import('@/views/ResourcesFormsView.vue')
const ResourcesProgramsView = () => import('@/views/ResourcesProgramsView.vue')
const BankStatementsSubmissionView = () => import('@/views/BankStatementsSubmissionView.vue')
const ImpersonationLoadingView = () => import('@/views/ImpersonationLoadingView.vue')
const HowToVideosView = () => import('@/views/tools/HowToVideos.vue')

const routes = [
  {
    path: '/',
    name: 'quick-pricer',
    redirect: '/quick-pricer',
    meta: {
      requiresAuth: false
    }
  },
  {
    path: '/quick-pricer',
    name: 'quick-pricer',
    component: QuickPricerView,
    meta: { 
      requiresAuth: false,
      title: 'Quick Pricer - Mort-Broker'
    }
  },
  {
    path: '/loan-details/:programId/:optionRate',
    name: 'loan-details',
    component: LoanDetailsView,
    props: (route: RouteLocationNormalized) => ({
      programId: route.params.programId as string,
      optionRate: parseFloat(route.params.optionRate as string)
    }),
    meta: {
      title: 'Loan Details - Mort-Broker',
      requiresAuth: true
    }
  },
  {
    path: '/403',
    name: 'forbidden',
    component: ForbiddenView,
    meta: { requiresErrorTrigger: true },
  },
  {
    path: '/419',
    name: 'page-expired',
    component: PageExpiredView,
    meta: { requiresErrorTrigger: true },
  },
  {
    path: '/500',
    name: 'server-error',
    component: ServerErrorView,
    meta: { requiresErrorTrigger: true },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: NotFoundView,
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

// Navigation guard
router.beforeEach(async (to, from, next) => {
  const auth = useAuthStore()
  const isAuthRoute = to.matched.some(record => record.meta.requiresAuth)
  const isLoginPage = to.path === '/login'
  const isErrorPage = to.matched.some(record => record.meta.requiresErrorTrigger)

  // Wait for auth initialization if not initialized
  if (!auth.isInitialized) {
    try {
      await auth.initialize()
    } catch (error) {
      console.error('Auth initialization failed in router guard:', error)
    }
  }

  // Prevent direct access to error pages
  if (isErrorPage && from.name === undefined) {
    // If there's a previous page in history, go back
    if (window.history.length > 1) {
      window.history.back()
    } else {
      // If no history, fallback to dashboard
      next({ path: '/quick-pricer' })
    }
    return
  }

  // Handle login page
  else if (isLoginPage && auth.isAuthenticated) {
    next({ path: '/quick-pricer' })
  }
  // Allow all other routes
  else {
    next()
  }
})

export default router 